import useEntryWorkspaceMutations from 'api/operation/entry/useEntryWorkspaceMutations';
import useGetEntryWorkspace from 'api/operation/entry/useGetEntryWorkspace';
import { useCallback, useEffect, useMemo, useState } from 'react';

const useEntryWorkspace = guid => {
  const { getEntryWorkspace, loading, workspace } = useGetEntryWorkspace();
  const mutations = useEntryWorkspaceMutations();
  const [actionLoading, setActionLoading] = useState(false);

  const canRefetch = useMemo(
    () => guid && location.pathname.startsWith('/operation/entry'),
    [guid],
  );

  useEffect(() => {
    if (!canRefetch) return;
    getEntryWorkspace(guid);
  }, [guid, getEntryWorkspace, canRefetch]);

  const refreshWorkspace = useCallback(() => {
    if (!guid) return Promise.resolve(null);
    return getEntryWorkspace(guid);
  }, [getEntryWorkspace, guid]);

  useEffect(() => {
    if (!canRefetch) return;
    window.addEventListener('refreshOperationList', refreshWorkspace);
    return () => {
      window.removeEventListener('refreshOperationList', refreshWorkspace);
    };
  }, [refreshWorkspace, canRefetch]);

  const withAction = useCallback(
    async action => {
      setActionLoading(true);
      try {
        const result = await action();
        if (result) await refreshWorkspace();
        return result;
      } finally {
        setActionLoading(false);
      }
    },
    [refreshWorkspace],
  );

  const baskets = useMemo(
    () =>
      (workspace?.shoppingCartList ?? []).map(basket => ({
        ...basket,
        id: basket.guid,
        productCount: basket.productPackagingList?.length ?? 0,
        statusLabel: basket.isLocked ? 'قفل شده' : 'فعال',
      })),
    [workspace?.shoppingCartList],
  );

  const dockProducts = useMemo(
    () =>
      (workspace?.docker ?? []).map(item => ({
        ...item,
        id: item.guid,
      })),
    [workspace?.docker],
  );

  const state = workspace?.state;
  const hasVoucher = Boolean(workspace?.hasVoucher);
  const voucherType = workspace?.voucherType ?? 0;
  const title = workspace?.title ?? '';

  const isLocked = state === 2 || state === 3;
  const canSubmitOperation = !isLocked;
  const canSubmitVoucher = state === 3 && !hasVoucher && voucherType !== 0;
  const canMutate = !isLocked;

  const removeFromBasket = useCallback(
    productGuid =>
      withAction(() => mutations.removeShoppingCartTask(productGuid)),
    [mutations, withAction],
  );

  const deleteBasket = useCallback(
    basketGuid => withAction(() => mutations.deleteShoppingCart(basketGuid)),
    [mutations, withAction],
  );

  const runTask = useCallback(
    basketGuid => withAction(() => mutations.runBasketTask(basketGuid)),
    [mutations, withAction],
  );

  const submitOperation = useCallback(
    () => withAction(() => mutations.submitOperation(guid)),
    [guid, mutations, withAction],
  );

  const submitVoucher = useCallback(
    () => withAction(() => mutations.submitVoucher(guid)),
    [guid, mutations, withAction],
  );

  return {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    hasVoucher,
    voucherType,
    baskets,
    dockProducts,
    refreshWorkspace,
    isLocked,
    canSubmitOperation,
    canSubmitVoucher,
    canMutate,
    removeFromBasket,
    deleteBasket,
    runTask,
    submitOperation,
    submitVoucher,
  };
};

export default useEntryWorkspace;
